/**
 * Local-only vs. server record identifiers (Szerviz / work sheets).
 *
 * A work sheet, customer or vehicle the technician creates while the API is
 * unreachable is stored in AsyncStorage immediately under a CLIENT-MINTED id, so
 * the list can render it before anything reached the server. That id does not
 * exist server-side: sending it to a by-id endpoint
 * (`/serviceModule/work-orders/<id>/…`) makes the backend try to cast it to a
 * Mongo ObjectId and answer an error — the production 500
 * "Cast to ObjectId failed for value offline:3a85c4fe-… at path _id" came from
 * exactly this class of id (the admin's variant of the same trick).
 *
 * Every service-module call therefore asks {@link isServerRecordId} first and
 * either creates the record instead of patching it, or keeps the change local.
 *
 * Historical note: local ids used to be a bare `Date.now().toString()`. Those are
 * still in the caches of installed apps, so the check is "is it a real ObjectId?"
 * rather than "does it carry our prefix?" — both old and new ids are covered.
 */

/** Canonical string form of a Mongo ObjectId — the only id the API accepts. */
export const SERVER_ID_PATTERN = /^[a-f\d]{24}$/i;

/** Prefix of newly minted local ids; self-describing in logs and caches. */
export const LOCAL_ID_PREFIX = 'local-';

/**
 * Does this id address a record that exists on the server?
 *
 * @param {*} id Identifier from state, cache or a screen param.
 * @returns {boolean} `true` only for a 24-hex ObjectId string.
 */
export const isServerRecordId = (id) => SERVER_ID_PATTERN.test(String(id ?? '').trim());

/**
 * Does this id belong to a record that only exists on this device (created
 * offline, not synced yet)? An empty id is neither — it is simply missing.
 *
 * @param {*} id Identifier from state, cache or a screen param.
 * @returns {boolean} `true` for a non-empty, non-ObjectId identifier.
 */
export const isLocalOnlyRecordId = (id) => {
    const value = String(id ?? '').trim();

    return value.length > 0 && !isServerRecordId(value);
};

/**
 * Mint an id for a record created on this device. Unique per call and clearly
 * marked as local, so it can never be mistaken for a server id.
 *
 * @returns {string} e.g. `local-1756718400000-4f2a1`.
 */
export const createLocalRecordId = () =>
    `${LOCAL_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** `code` carried by the error thrown for an unsynced record. */
export const UNSYNCED_RECORD_ERROR_CODE = 'UNSYNCED_RECORD';

/**
 * Build the error an API helper rejects with when asked to act on a record that
 * has not reached the server yet. Screens catch it, recognise `code` and show the
 * "not synced yet" notice instead of a raw backend message.
 *
 * @param {*} id The local-only identifier that was passed in.
 * @returns {Error} Error tagged with `code` / `unsynced` / `id`.
 */
export const createUnsyncedRecordError = (id) => {
    const error = new Error('This record has not been synced to the server yet.');

    error.code = UNSYNCED_RECORD_ERROR_CODE;
    error.unsynced = true;
    error.id = String(id ?? '');

    return error;
};

export default {
    SERVER_ID_PATTERN,
    LOCAL_ID_PREFIX,
    UNSYNCED_RECORD_ERROR_CODE,
    isServerRecordId,
    isLocalOnlyRecordId,
    createLocalRecordId,
    createUnsyncedRecordError,
};
